import { initializeApp } from 'firebase/app';
import { 
  getAuth, 
  GoogleAuthProvider, 
  OAuthProvider,
  signInWithPopup,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  User
} from 'firebase/auth';
import {
  getFirestore,
  doc,
  setDoc,
  getDoc,
  updateDoc,
  serverTimestamp
} from 'firebase/firestore';

// Your web app's Firebase configuration
// Replace with your own Firebase config
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);

// Auth providers
const googleProvider = new GoogleAuthProvider();
const microsoftProvider = new OAuthProvider('microsoft.com');

// Configure Google Provider with proper settings
googleProvider.addScope('https://www.googleapis.com/auth/userinfo.email');
googleProvider.addScope('https://www.googleapis.com/auth/userinfo.profile');
googleProvider.setCustomParameters({
  prompt: 'select_account'
});

// Function to validate if email domain is allowed
const isAllowedDomain = (email: string): boolean => {
  const allowedDomains = ['@stitchdesignco.com', '@sdcopartners.com'];
  const emailLower = email.toLowerCase();
  return allowedDomains.some(domain => emailLower.endsWith(domain));
};

// Function to create or update a user document in Firestore
export const createOrUpdateUserDocument = async (user: User) => {
  if (!user) return;
  
  // Check if user's email domain is allowed
  if (!user.email || !isAllowedDomain(user.email)) {
    throw new Error('Access restricted to @stitchdesignco.com and @sdcopartners.com email addresses only.');
  }
  
  try {
    // First, check if a user with this email already exists (from admin creation)
    const { collection, query, where, getDocs, deleteDoc, or } = await import('firebase/firestore');
    const usersRef = collection(db, 'users');
    
    // Get the username part of the email for cross-domain matching
    const username = user.email.split('@')[0].toLowerCase();
    const currentEmail = user.email.toLowerCase();
    
    // Look for users with either the exact email OR the same username with the other domain
    const stitchEmail = `${username}@stitchdesignco.com`;
    const sdcoEmail = `${username}@sdcopartners.com`;
    
    const emailQuery = query(
      usersRef, 
      or(
        where('email', '==', stitchEmail),
        where('email', '==', sdcoEmail)
      )
    );
    const existingUsers = await getDocs(emailQuery);
    
    let existingUserData = null;
    let existingDocToDelete = null;
    
    // If there are existing user documents, find the best one to merge
    if (!existingUsers.empty) {
      // Prioritize exact email match first, then cross-domain match
      const exactMatch = existingUsers.docs.find(doc => doc.data().email === currentEmail);
      const crossDomainMatch = existingUsers.docs.find(doc => doc.data().email !== currentEmail);
      
      const docToMerge = exactMatch || crossDomainMatch;
      
      if (docToMerge) {
        existingUserData = docToMerge.data();
        
        // Delete the old document if it has a different ID than the auth UID
        if (docToMerge.id !== user.uid) {
          const mergeType = exactMatch ? 'exact email' : 'cross-domain';
          console.log(`Merging existing user data for ${user.email} from ${docToMerge.data().email} (${mergeType} match) - ${docToMerge.id} to ${user.uid}`);
          existingDocToDelete = docToMerge;
        }
        
        // If there are multiple documents (both domains), clean up the others
        existingUsers.docs.forEach(doc => {
          if (doc.id !== user.uid && doc.id !== docToMerge.id) {
            console.log(`Cleaning up duplicate user document: ${doc.data().email} (${doc.id})`);
            deleteDoc(doc.ref);
          }
        });
      }
    }
    
    // Try to delete the document we're merging from (after we've extracted the data)
    // This may fail due to permissions if the user doesn't own the old document,
    // so we wrap it in a try/catch and continue regardless
    if (existingDocToDelete) {
      try {
        await deleteDoc(existingDocToDelete.ref);
        console.log(`Deleted old user document ${existingDocToDelete.id}`);
      } catch (deleteError) {
        // This is expected when the old doc was created by admin
        // The user doesn't have permission to delete it, but that's okay
        // We'll still create/update the correct document with their auth UID
        console.log(`Could not delete old user document ${existingDocToDelete.id} (will be orphaned):`, deleteError);
      }
    }
    
    const userRef = doc(db, 'users', user.uid);
    const userSnap = await getDoc(userRef);
    
    if (!userSnap.exists()) {
      // Create a new user document, preserving admin-created data if it exists
      console.log(`Creating new user document for ${user.email}`);
      
      // Determine role: preserve existing role, or default based on email
      const superAdmins = ['hunter@stitchdesignco.com', 'laine@stitchdesignco.com'];
      const defaultRole = superAdmins.includes(user.email.toLowerCase()) ? 'admin' : 'employee';
      
      const userData = {
        email: user.email.toLowerCase(),
        displayName: user.displayName || existingUserData?.displayName || user.email.split('@')[0],
        photoURL: user.photoURL,
        role: existingUserData?.role || defaultRole,
        // Handle both new multiple managers format and legacy single manager
        managers: existingUserData?.managers || [],
        // Keep legacy fields for backward compatibility
        managerId: existingUserData?.managerId || null,
        managerName: existingUserData?.managerName || null,
        isActive: true,
        createdAt: existingUserData?.createdAt || serverTimestamp(),
        lastLogin: serverTimestamp(),
        // Preserve peer review status
        upForReview: existingUserData?.upForReview || false,
        reviewCycle: existingUserData?.reviewCycle || null,
        reviewCycleStart: existingUserData?.reviewCycleStart || null,
        // Preserve engagement pulse check status
        upForPulseCheck: existingUserData?.upForPulseCheck || false,
        pulseCheckCycleStart: existingUserData?.pulseCheckCycleStart || null,
        // Preserve HR flags
        isHR: existingUserData?.isHR || false,
        isSuperHR: existingUserData?.isSuperHR || false,
        // Preserve admin-added metadata
        addedBy: existingUserData?.addedBy || null
      };
      
      await setDoc(userRef, userData);
      console.log(`User document created for ${user.email}${existingUserData ? ' (merged from admin-created record)' : ''}`);
    } else {
      // Update the existing user document
      console.log(`Updating user document for ${user.email}`);
      await updateDoc(userRef, {
        displayName: user.displayName || userSnap.data()?.displayName,
        photoURL: user.photoURL,
        lastLogin: serverTimestamp()
      });
    }
    
    return userRef;
  } catch (error) {
    console.error('Error creating/updating user document:', error);
    throw error;
  }
};

// Auth helper functions
export const signInWithEmail = async (email: string, password: string) => {
  try {
    // Check domain before attempting authentication
    if (!isAllowedDomain(email)) {
      throw new Error('Access restricted to @stitchdesignco.com and @sdcopartners.com email addresses only.');
    }
    
    const result = await signInWithEmailAndPassword(auth, email, password);
    await createOrUpdateUserDocument(result.user);
    return result.user;
  } catch (error) {
    console.error('Error signing in with email:', error);
    throw error;
  }
};

export const signUpWithEmail = async (email: string, password: string) => {
  try {
    // Check domain before attempting authentication
    if (!isAllowedDomain(email)) {
      throw new Error('Access restricted to @stitchdesignco.com and @sdcopartners.com email addresses only.');
    }
    
    const result = await createUserWithEmailAndPassword(auth, email, password);
    await createOrUpdateUserDocument(result.user);
    return result.user;
  } catch (error) {
    console.error('Error signing up with email:', error);
    throw error;
  }
};

export const signInWithGoogle = async () => {
  try {
    console.log('Starting Google sign-in process');
    const result = await signInWithPopup(auth, googleProvider);
    console.log('Google sign-in successful');
    await createOrUpdateUserDocument(result.user);
    return result.user;
  } catch (error: any) {
    console.error('Error signing in with Google:', error);
    
    // Add specific error handling
    if (error.code === 'auth/configuration-not-found') {
      console.error('Firebase configuration not found. Check if Google sign-in is enabled in Firebase console.');
      throw new Error('Google authentication is not properly configured. Please check your Firebase console settings.');
    }
    
    if (error.code === 'auth/popup-closed-by-user') {
      throw new Error('Sign-in popup was closed before completing the sign-in process.');
    }
    
    if (error.code === 'auth/popup-blocked') {
      throw new Error('Sign-in popup was blocked by the browser. Please allow popups for this site.');
    }
    
    throw error;
  }
};

export const signInWithMicrosoft = async () => {
  try {
    const result = await signInWithPopup(auth, microsoftProvider);
    await createOrUpdateUserDocument(result.user);
    return result.user;
  } catch (error) {
    console.error('Error signing in with Microsoft:', error);
    throw error;
  }
};

export const logOut = async () => {
  try {
    await signOut(auth);
  } catch (error) {
    console.error('Error signing out:', error);
    throw error;
  }
};

export const getCurrentUser = () => {
  return new Promise((resolve) => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      unsubscribe();
      if (user) {
        await createOrUpdateUserDocument(user);
      }
      resolve(user);
    });
  });
};

// Function to get a user's profile from Firestore
export const getUserProfile = async (uid: string) => {
  const userRef = doc(db, 'users', uid);
  const userSnap = await getDoc(userRef);
  if (userSnap.exists()) {
    return userSnap.data();
  }
  return null;
};

export { auth, db, googleProvider, microsoftProvider };
